import type { Graphics } from 'pixi.js';
import { NIGHT_END, NIGHT_START } from '../../shared/palette';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import { shadowAt } from '../ground-sun';
import { HANDS, HAT_AT, TAIL_SWAY, UNIT } from './echassier-paint';

// Heights in these units, cast this far along the light per unit: the hat's shadow lies far away.
const HEIGHT_UNIT = 1.76;
const HIP = 70;
// The top of his hat, the far end of his shadow.
const SHADOW_TOP = HIP + 56;
const HIP_ACROSS = 4;
const SAMPLES = 12;
const SHADE = 0x000000;
export const SHADOW_ALPHA = 0.7;

export interface StiltFoot {
  x: number;
  y: number;
  // Raised off the ground, in world units: its shadow slides along the light.
  lift: number;
}

export interface StiltPose {
  x: number;
  y: number;
  scale: number;
  // Where he faces, as his drawing from above: his shadow turns with him.
  facing: number;
  // Unit vector of the light, from his feet towards his hat's shadow.
  lightX: number;
  lightY: number;
  feet: readonly [StiltFoot, StiltFoot];
  // In beats, for the hands and tails that swing as his drawing's do.
  beats: number;
  calm: boolean;
  length: number;
  // How much of his height it reaches, from his feet: the charge fills it up to his hat.
  upTo?: number;
  color?: number;
}

// A level slice of him: an ellipse in his drawing's frame (along where he faces, across it) at a height.
interface Slice {
  readonly height: number;
  readonly along: number;
  readonly halfAlong: number;
  readonly halfAcross: number;
}

function slice(height: number, halfAlong: number, halfAcross = halfAlong, along = 0): Slice {
  return { height, along, halfAlong, halfAcross };
}

// Each run is a solid from one slice to the next, the same sizes as his drawing from above.
const BODY: readonly (readonly Slice[])[] = [
  [
    slice(HIP, 4.5, 6.5),
    slice(HIP + 10, 5, 8.5),
    slice(HIP + 22, 7.5, 13),
    slice(HIP + 26, 6, 10.5),
  ],
  [slice(HIP + 25, 2.6), slice(HIP + 30, 2.6)],
  [slice(HIP + 30, 4.5), slice(HIP + 34, 6), slice(HIP + 38, 5)],
  [slice(HIP + 38, 10.4, 10.4, HAT_AT), slice(HIP + 40, 10.4, 10.4, HAT_AT)],
  [slice(HIP + 40, 6.1, 6.1, HAT_AT), slice(HIP + 56, 6.6, 6.6, HAT_AT)],
];
const SHOULDER = { along: 1, across: 12, height: HIP + 21 } as const;
const HAND_HEIGHT = HIP + 6;
// His tailcoat's tails, as drawn from above, hanging from his waist.
const TAIL: readonly (readonly [number, number, number])[] = [
  [-5, 4, HIP + 18],
  [-22, 8.5, HIP + 6],
  [-11, 11.5, HIP + 12],
];
const WIDTH = { stilt: 2.4, leg: 4.4, arm: 2.4, hand: 3.2, foot: 1.8 } as const;
const LEG_FROM = 0.62;

// The light of the hour: the sun's by day, swinging from dusk to dawn through the night.
export function lightAngle(fraction: number): number {
  const sun = shadowAt(fraction);
  if (fraction < NIGHT_START || fraction >= NIGHT_END) {
    return sun.angle;
  }
  const dusk = shadowAt(0).angle;
  const dawn = shadowAt(NIGHT_END).angle;
  return lerp(
    dusk,
    dawn,
    smoothstep(clamp01((fraction - NIGHT_START) / (NIGHT_END - NIGHT_START))),
  );
}

type Point = readonly [number, number];

function cross(o: Point, a: Point, b: Point): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

// One side of the convex hull of points sorted by x: a monotone chain.
function chain(points: readonly Point[]): Point[] {
  const kept: Point[] = [];
  for (const point of points) {
    let last = kept.at(-1);
    let before = kept.at(-2);
    while (before !== undefined && last !== undefined && cross(before, last, point) <= 0) {
      kept.pop();
      last = before;
      before = kept.at(-2);
    }
    kept.push(point);
  }
  return kept.slice(0, -1);
}

function hull(points: Point[]): Point[] {
  points.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  return [...chain(points), ...chain([...points].reverse())];
}

// His body from above cast along the light: each level slice of him lands as it is, turned as he
// faces, the higher the farther. Drawn opaque: the caller lays it at SHADOW_ALPHA in one pass.
export function drawStiltShadow(g: Graphics, pose: StiltPose): void {
  const { x, y, lightX, lightY, feet, calm } = pose;
  const flat = UNIT * pose.scale;
  const cast = HEIGHT_UNIT * pose.scale * pose.length;
  const cos = Math.cos(pose.facing);
  const sin = Math.sin(pose.facing);
  const sway = Math.sin(pose.beats * Math.PI) * (calm ? 0.4 : 1);
  const top = SHADOW_TOP * (pose.upTo ?? 1);
  const color = pose.color ?? SHADE;
  const at = (along: number, across: number, height: number): Point => [
    x + (along * cos - across * sin) * flat + lightX * Math.min(height, top) * cast,
    y + (along * sin + across * cos) * flat + lightY * Math.min(height, top) * cast,
  ];
  const ring = (part: Slice): Point[] =>
    Array.from({ length: SAMPLES }, (_, index) => {
      const turn = (index / SAMPLES) * Math.PI * 2;
      return at(
        part.along + Math.cos(turn) * part.halfAlong,
        Math.sin(turn) * part.halfAcross,
        part.height,
      );
    });
  const fill = (points: readonly Point[]): void => {
    g.poly(points.flat()).fill({ color });
  };
  const segment = (from: Point, to: Point, width: number): void => {
    g.moveTo(from[0], from[1])
      .lineTo(to[0], to[1])
      .stroke({ width: width * flat, color, cap: 'round' });
  };

  feet.forEach((foot, index) => {
    const side = index === 0 ? -1 : 1;
    const ground: Point = [foot.x + lightX * foot.lift, foot.y + lightY * foot.lift];
    const hip = at(0, side * HIP_ACROSS, HIP);
    const reach = Math.min(1, top / HIP);
    const end: Point = [lerp(ground[0], hip[0], reach), lerp(ground[1], hip[1], reach)];
    g.circle(ground[0], ground[1], WIDTH.foot * flat).fill({ color });
    segment(ground, end, WIDTH.stilt);
    if (reach > LEG_FROM) {
      segment(
        [lerp(ground[0], hip[0], LEG_FROM), lerp(ground[1], hip[1], LEG_FROM)],
        end,
        WIDTH.leg,
      );
    }
  });
  for (const run of BODY) {
    run.slice(1).forEach((upper, index) => {
      const lower = run[index] ?? upper;
      if (lower.height < top) {
        fill(hull([...ring(lower), ...ring(upper)]));
      }
    });
  }
  if (top <= HIP) {
    return;
  }
  const swing = sway * TAIL_SWAY;
  for (const side of [-1, 1]) {
    fill(
      TAIL.map(([along, across, height]) => {
        const turnedAlong = along * Math.cos(swing) - side * across * Math.sin(swing);
        const turnedAcross = along * Math.sin(swing) + side * across * Math.cos(swing);
        return at(turnedAlong, turnedAcross, height);
      }),
    );
    if (top <= SHOULDER.height) {
      continue;
    }
    const hand = at(HANDS.along - side * sway * HANDS.swing, side * HANDS.across, HAND_HEIGHT);
    segment(at(SHOULDER.along, side * SHOULDER.across, SHOULDER.height), hand, WIDTH.arm);
    g.circle(hand[0], hand[1], WIDTH.hand * flat).fill({ color });
  }
}

// Where the shadow of a point `height` above the ground falls, in world units.
export function shadowReach(scale: number, height: number): number {
  return height * HEIGHT_UNIT * scale;
}

export const HAT_HEIGHT = HIP + 50;
